import { useEffect, useState } from "react";
import { Link2, Pencil, Tags } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate } from "@/lib/api";
import { PageHeader, EmptyState, Money, StatusBadge } from "@/components/common";
import { can } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { CarFormDialog } from "@/pages/Cars";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function Pricelist() {
  const { user } = useAuth();
  const [cars, setCars] = useState(null);
  const [branches, setBranches] = useState([]);
  const [editCar, setEditCar] = useState(null);

  const load = () => {
    Promise.all([api.get("/cars?status=siap_jual,booking"), api.get("/branches")]).then(([c, b]) => {
      setCars(c.data);
      setBranches(b.data);
    });
  };
  useEffect(() => { load(); }, []);

  const copyLink = async () => {
    const url = `${window.location.origin}/pricelist-publik`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link pricelist publik disalin");
    } catch {
      toast.info(url);
    }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "-";

  const togglePublic = async (c, v) => {
    try {
      await api.put(`/cars/${c.id}`, { show_in_pricelist: v });
      toast.success(v ? "Ditampilkan di pricelist publik" : "Disembunyikan dari pricelist publik");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="pricelist-page">
      <PageHeader title="Pricelist" subtitle="Unit Siap Jual & Booking — sumber data yang sama dengan pricelist publik">
        <Button data-testid="copy-public-link-button" onClick={copyLink} variant="outline" className="border-[#26262E] text-zinc-300 hover:bg-[#121218]">
          <Link2 className="w-4 h-4 mr-1" /> Salin Link Publik
        </Button>
        <a
          data-testid="open-public-link"
          href="/pricelist-publik"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] text-sm font-semibold transition-colors duration-200"
        >
          Lihat Halaman Publik
        </a>
      </PageHeader>

      {!cars ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : cars.length === 0 ? (
        <EmptyState icon={Tags} title="Belum ada mobil Siap Jual" desc="Tandai mobil sebagai Siap Jual lalu isi harganya" />
      ) : (
        <>
        <p className="text-xs text-zinc-400 mb-3" data-testid="pricelist-summary">
          <span className="text-emerald-500 font-semibold">{cars.filter((c) => c.show_in_pricelist !== false).length}</span> tampil di pricelist publik dari {cars.length} unit
          {cars.filter((c) => c.show_in_pricelist === false).length > 0 && (
            <span className="text-zinc-500"> · {cars.filter((c) => c.show_in_pricelist === false).length} disembunyikan</span>
          )}
        </p>
        <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-x-auto fade-up">
          <table className="w-full text-sm min-w-[960px]" data-testid="pricelist-table">
            <thead>
              <tr className="border-b border-[#1A1A22] text-left">
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Mobil</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Status</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Cabang</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Pajak</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Harga Cash</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Harga Kredit</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Paket Kredit</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-center">Tampil Publik</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {cars.map((c) => (
                <tr key={c.id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                  <td className="px-4 py-3">
                    <p className="font-medium text-zinc-100">{c.merk} {c.model} {c.color} {c.year} {c.plate}</p>
                    <p className="text-xs text-zinc-500">{new Intl.NumberFormat("id-ID").format(c.km || 0)} km</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-zinc-400">{branchName(c.branch_id)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">
                    {c.tax_status ? `${c.tax_status === "hidup" ? "Hidup" : "Mati"}${c.tax_due_date ? ` s.d. ${c.tax_due_date}` : ""}` : "-"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.asking_cash_price ? <Money value={c.asking_cash_price} className="text-blue-500 font-semibold" /> : <span className="text-zinc-600 text-xs">Belum diisi</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.asking_credit_price ? <Money value={c.asking_credit_price} className="text-zinc-100" /> : <span className="text-zinc-600">-</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">
                    {(c.credit_packages || []).length
                      ? (c.credit_packages || []).map((p, i) => (
                          <p key={i}>{p.leasing_name}: TDP {new Intl.NumberFormat("id-ID").format(p.tdp || 0)} · {p.installments.map((ins) => `${new Intl.NumberFormat("id-ID").format(ins.amount)}×${ins.tenor}`).join(" / ")}</p>
                        ))
                      : "-"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {can(user, "kelola_mobil") ? (
                      <div className="flex items-center justify-center gap-2">
                        <Checkbox
                          data-testid={`toggle-pricelist-${c.id}`}
                          checked={c.show_in_pricelist !== false}
                          onCheckedChange={(v) => togglePublic(c, !!v)}
                        />
                        <span className={`text-[11px] font-medium ${c.show_in_pricelist !== false ? "text-emerald-500" : "text-zinc-500"}`}>
                          {c.show_in_pricelist !== false ? "Tampil" : "Disembunyikan"}
                        </span>
                      </div>
                    ) : (
                      <span className={`text-[11px] font-medium ${c.show_in_pricelist !== false ? "text-emerald-500" : "text-zinc-500"}`}>
                        {c.show_in_pricelist !== false ? "Tampil" : "Disembunyikan"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {can(user, "kelola_mobil") && (
                      <button data-testid={`edit-price-${c.id}`} onClick={() => setEditCar(c)} className="p-1.5 text-zinc-500 hover:text-blue-500 transition-colors duration-200" title="Edit harga">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <CarFormDialog open={!!editCar} onOpenChange={(v) => !v && setEditCar(null)} branches={branches} car={editCar} onSaved={load} />
    </div>
  );
}
